import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress, IconButton,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {
  resolveSliderKind,
  SLIDER_CONFIGS,
  clampToConfig,
  normalizeItem,
  KpiSliderRow,
} from '../../../Assessment/kpiSliders';

const backendBaseUrl = (process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com').replace(/\/$/, '');

// Read a cookie value by name without an external dependency
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
};

const useStyles = makeStyles((theme) => ({
  errorText: {
    color: theme.palette.error.main,
    fontSize: 12,
  },
  backButton: {
    color: '#002D55',
    borderColor: '#002D55',
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    paddingLeft: 18,
    paddingRight: 18,
    '&:hover': {
      color: '#FF7F27',
      borderColor: '#FF7F27',
      backgroundColor: 'transparent',
    },
  },
  nextButton: {
    backgroundColor: '#FF7F27',
    color: '#fff',
    fontWeight: 700,
    borderRadius: 999,
    textTransform: 'none',
    boxShadow: 'none',
    paddingLeft: 22,
    paddingRight: 22,
    '&:hover': {
      backgroundColor: '#002D55',
      boxShadow: 'none',
    },
  },
  saveButton: {
    backgroundColor: '#002D55',
    color: '#fff',
    fontWeight: 700,
    borderRadius: 999,
    textTransform: 'none',
    boxShadow: 'none',
    paddingLeft: 22,
    paddingRight: 22,
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '1px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 5px) clamp(7px, 0.85vw, 15px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '1px solid #FF7F27',
      boxShadow: 'none',
    },
  },
}));

/**
 * ClinicianFollowupDialog
 *
 * Props:
 *   open           – boolean
 *   onClose        – fn()
 *   onSaved        – fn() called after successful POST
 *   patientId      – string (user UUID)
 *   diagnosisIdentifier – string
 *   treatmentId    – string (optional)
 *   kpiItems       – array of { question_identifier, question_type, label, numeric_value }
 *                    Pre-populated from the parent (batch_metrics or kpi_items endpoint).
 *                    If empty, the dialog fetches /treatment/treatments/<treatmentId>/kpi_items/
 */
const ClinicianFollowupDialog = ({
  open,
  onClose,
  onSaved,
  patientId,
  diagnosisIdentifier,
  treatmentId,
  kpiItems: propKpiItems,
  userToken: propUserToken,
  initialStep,
}) => {
  const classes = useStyles();
  const [kpiItems, setKpiItems] = useState([]);
  const [values, setValues] = useState({});
  const [labels, setLabels] = useState({}); // editable goal text (function only)
  const [editingLabel, setEditingLabel] = useState({}); // map qid -> bool
  const [step, setStep] = useState(0); // 0 = pain/frequency, 1 = function goals
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Token resolution: explicit prop > Redux auth store > cookie. The
  // cookie path was the only source originally and broke whenever the
  // app stored its DRF token in Redux/localStorage instead of a
  // cookie, producing 401s on /kpi_items/.
  const reduxToken = useSelector((state) => (state && state.auth && state.auth.token) || null);
  const token = propUserToken || reduxToken || getCookie('token');
  const authHeader = token ? { Authorization: `Token ${token}` } : {};

  const seedFromItems = (items, startStep) => {
    setKpiItems(items);
    const initial = {};
    const initialLabels = {};
    items.forEach((it) => {
      const cfg = SLIDER_CONFIGS[resolveSliderKind(it)];
      initial[it.question_identifier] = clampToConfig(it.numeric_value, cfg);
      initialLabels[it.question_identifier] = it.label || '';
    });
    setValues(initial);
    setLabels(initialLabels);
    setEditingLabel({});
    setStep(typeof startStep === 'number' && startStep >= 0 ? startStep : 0);
  };

  // Load KPI items — use prop if provided, else fetch from treatment
  useEffect(() => {
    if (!open) return;
    setError('');
    const startStep = (typeof initialStep === 'number' && initialStep >= 0) ? initialStep : 0;

    if (propKpiItems && propKpiItems.length > 0) {
      // NPS is patient-only — exclude from clinician follow-ups so clinician
      // ratings can't bias the patient's self-reported satisfaction score.
      seedFromItems(
        propKpiItems
          .map(normalizeItem)
          .filter((i) => i.question_identifier)
          .filter((i) => resolveSliderKind(i) !== 'nps'),
        startStep
      );
      return;
    }

    if (!treatmentId) {
      setKpiItems([]);
      setStep(startStep);
      return;
    }

    setLoading(true);
    // `in_latest_journal=1` filters server-side to KPIs that are part of
    // the most recent TreatmentJournalVersion the clinician saved, so the
    // follow-up dialog mirrors what's actually in the patient's latest
    // journal (e.g. omits NPS-only entries that were never published).
    fetch(`${backendBaseUrl}/treatment/treatments/${treatmentId}/kpi_items/?in_latest_journal=1`, { headers: authHeader })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        const items = (data.kpi_items || [])
          .map(normalizeItem)
          .filter((i) => i.question_identifier)
          // NPS is patient-only — exclude from clinician follow-ups so
          // clinician ratings can't bias the patient's self-reported score.
          .filter((i) => resolveSliderKind(i) !== 'nps');
        seedFromItems(items, startStep);
      })
      .catch(() => setError('Failed to load KPI items.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, treatmentId, propKpiItems]);

  const handleSliderChange = useCallback((qId, val) => {
    setValues((prev) => ({ ...prev, [qId]: val }));
  }, []);

  const handleLabelChange = useCallback((qId, val) => {
    setLabels((prev) => ({ ...prev, [qId]: val }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!patientId || !diagnosisIdentifier) {
      setError('Patient or diagnosis missing.');
      return;
    }

    const measurements = kpiItems.map((item) => {
      const kind = resolveSliderKind(item);
      const isFunction = kind === 'function';
      const v = values[item.question_identifier];
      return {
        question_identifier: item.question_identifier,
        question_type: item.question_type,
        // For function goals, persist the (possibly edited) goal text so
        // the next status report / patient row reflects the rename. For
        // pain/frequency/NPS the original label is informational only.
        label: isFunction ? (labels[item.question_identifier] || item.label) : item.label,
        text_value: isFunction ? (labels[item.question_identifier] || item.label) : undefined,
        value: v != null ? v : null,
      };
    }).filter((m) => m.question_identifier && m.value != null);

    if (measurements.length === 0) {
      setError('No measurements to save.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const resp = await fetch('/question-handler/clinician-followup/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          user_id: patientId,
          diagnosis_identifier: diagnosisIdentifier,
          treatment_id: treatmentId || undefined,
          measurements,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setError(body.error || `Save failed (${resp.status})`);
      } else {
        if (onSaved) onSaved();
        onClose();
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [kpiItems, values, labels, patientId, diagnosisIdentifier, treatmentId, onSaved, onClose, authHeader]);

  const renderItem = (item) => {
    const qid = item.question_identifier;
    return (
      <KpiSliderRow
        item={item}
        value={values[qid]}
        onChange={handleSliderChange}
        labelText={labels[qid]}
        onLabelChange={handleLabelChange}
        isEditingLabel={!!editingLabel[qid]}
        onLabelEditToggle={(q, b) => setEditingLabel((p) => ({ ...p, [q]: b }))}
        allowGoalEdit
        showBaseline
      />
    );
  };

  // Split items into the two wizard step groups. Pain (current/best/worst)
  // and frequency baselines on group 1; functional goals (and any
  // generic/NPS-style follow-ups) on group 2. Each group is then chunked
  // into substeps of at most MAX_PER_STEP items so very long lists don't
  // produce a wall of sliders. Substeps share the parent group's title and
  // a `subIndex / subCount` pair used by the stepper to render mini-dots.
  const MAX_PER_STEP = 4;
  const isStep1Item = (item) => {
    const k = resolveSliderKind(item);
    return k === 'pain_current' || k === 'pain_atbest' || k === 'pain_atworst' || k === 'pain_custom' || k === 'frequency';
  };
  const step1Items = kpiItems.filter(isStep1Item);
  const step2Items = kpiItems.filter((it) => !isStep1Item(it));

  const chunk = (arr, size) => {
    if (arr.length === 0) return [[]];
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const groups = [
    { title: 'Symptom Baselines', items: step1Items },
    { title: 'Functional Goals', items: step2Items },
  ];

  // Flat list of substeps actually rendered by the wizard. Empty groups
  // still contribute a single placeholder substep so the main stepper dot
  // remains visible (greyed out) — the navigation helper below skips it.
  const stepConfig = [];
  groups.forEach((g, gIdx) => {
    const pages = chunk(g.items, MAX_PER_STEP);
    pages.forEach((pageItems, sIdx) => {
      stepConfig.push({
        groupIndex: gIdx,
        subIndex: sIdx,
        subCount: pages.length,
        title: g.title,
        items: pageItems,
      });
    });
  });

  // If a step has no items, skip it on next/back navigation rather than
  // showing an empty page. Clicking Save from any step writes ALL
  // measurements (both buckets) so users don't have to traverse an
  // empty step just to submit.
  const findNextNonEmpty = (from, dir) => {
    let i = from + dir;
    while (i >= 0 && i < stepConfig.length) {
      if (stepConfig[i].items.length > 0) return i;
      i += dir;
    }
    return -1;
  };

  const goNext = () => {
    const next = findNextNonEmpty(step, +1);
    if (next >= 0) setStep(next);
  };
  const goBack = () => {
    const prev = findNextNonEmpty(step, -1);
    if (prev >= 0) setStep(prev);
  };

  const hasNext = findNextNonEmpty(step, +1) >= 0;
  const hasBack = findNextNonEmpty(step, -1) >= 0;

  // Snap the wizard to the first non-empty step whenever items load —
  // e.g. a treatment with no pain baselines should open on the function
  // goals step instead of an empty pain page.
  useEffect(() => {
    if (kpiItems.length === 0) return;
    if (stepConfig[step] && stepConfig[step].items.length === 0) {
      const fallback = stepConfig.findIndex((s) => s.items.length > 0);
      if (fallback >= 0 && fallback !== step) setStep(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiItems.length]);

  const current = stepConfig[step] || stepConfig[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          borderRadius: 18,
          backgroundColor: '#f7faff',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        disableTypography
        style={{ paddingBottom: 8, backgroundColor: '#f7faff' }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 16, marginBottom: 16 }}>
          <Typography variant="h6" style={{ fontWeight: 700, color: '#002D55', textAlign: 'left' }}>
            📊 Record Follow-up
          </Typography>
          <IconButton onClick={onClose} style={{ color: '#64748B', padding: 6 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="flex-start" justifyContent="center">
          {groups.map((g, gIdx) => {
            // Find the flat step indices that belong to this group so we can
            // figure out whether the user has passed the group entirely
            // (done), is currently inside it (active), or hasn't reached it
            // yet — and which substep dot to highlight.
            const subIndices = stepConfig
              .map((s, idx) => (s.groupIndex === gIdx ? idx : -1))
              .filter((idx) => idx >= 0);
            const firstFlat = subIndices[0];
            const lastFlat = subIndices[subIndices.length - 1];
            const empty = g.items.length === 0;
            const isActive = step >= firstFlat && step <= lastFlat;
            const isDone = step > lastFlat;
            const dotColor = isDone
              ? '#002D55'
              : isActive
                ? '#FF7F27'
                : (empty ? '#e6eaf0' : '#bdbdbd');
            const activeSub = isActive ? step - firstFlat : -1;
            return (
              <React.Fragment key={g.title}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  style={{ minWidth: 140 }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: dotColor,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 14,
                      boxShadow: isActive ? '0 6px 18px rgba(0,0,0,0.16)' : 'none',
                    }}
                    title={g.title}
                  >
                    {gIdx + 1}
                  </Box>
                  <Typography
                    style={{
                      marginTop: 6,
                      fontWeight: isActive ? 700 : 600,
                      color: (isActive || isDone) ? '#002D55' : '#94a3b8',
                      fontSize: 13,
                      textAlign: 'center',
                    }}
                  >
                    {g.title}
                  </Typography>
                  {subIndices.length > 1 && (
                    <Box display="flex" alignItems="center" style={{ marginTop: 4, gap: 4 }}>
                      {subIndices.map((flatIdx, sIdx) => {
                        const subDone = isDone || (isActive && sIdx < activeSub);
                        const subActive = isActive && sIdx === activeSub;
                        const subBg = subActive
                          ? '#FF7F27'
                          : subDone
                            ? '#002D55'
                            : '#cbd5e1';
                        return (
                          <Box
                            key={flatIdx}
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: subBg,
                              transition: 'background 0.15s',
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
                {gIdx < groups.length - 1 && (
                  <Box
                    style={{
                      width: 56,
                      height: 2,
                      marginTop: 16,
                      background: isDone ? '#002D55' : '#e6eaf0',
                      borderRadius: 1,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </DialogTitle>
      <DialogContent dividers={false} style={{ backgroundColor: '#f7faff', paddingTop: 16 }}>
        {!loading && current && current.subCount > 1 && (
          <Typography
            style={{
              color: '#64748B',
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              textAlign: 'center',
            }}
          >
            {current.title} · {current.subIndex + 1} / {current.subCount}
          </Typography>
        )}
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} style={{ color: '#FF7F27' }} />
          </Box>
        )}
        {!loading && kpiItems.length === 0 && (
          <Typography style={{ padding: '16px 0', color: '#64748B' }}>
            No KPI items found for this patient.
          </Typography>
        )}
        {!loading && kpiItems.length > 0 && current.items.length === 0 && (
          <Typography style={{ padding: '16px 0', color: '#64748B' }}>
            No items in this section.
          </Typography>
        )}
        {!loading && current.items.map((item) => (
          <React.Fragment key={item.question_identifier}>
            {renderItem(item)}
          </React.Fragment>
        ))}
        {error && <Typography className={classes.errorText}>{error}</Typography>}
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px', gap: 8, backgroundColor: '#f7faff' }}>
        <Button
          onClick={onClose}
          disabled={saving}
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Box flex={1} />
        {hasBack && (
          <Button
            onClick={goBack}
            disabled={saving}
            variant="outlined"
            className={classes.backButton}
          >
            Back
          </Button>
        )}
        {hasNext ? (
          <Button
            onClick={goNext}
            disabled={saving || loading || kpiItems.length === 0}
            variant="contained"
            className={classes.nextButton}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            disabled={saving || loading || kpiItems.length === 0}
            variant="contained"
            className={classes.saveButton}
          >
            {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Save Follow-up'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClinicianFollowupDialog;
